import React, { useState, useRef, useEffect } from 'react';
import { audioPortfolio } from '../data/mock';
import { Play, Pause, Music2, Clock, Tag } from 'lucide-react';

const AudioPortfolio = () => {
  const [playingId, setPlayingId] = useState(null);
  const audioRefs = useRef({});

  useEffect(() => {
    // Pause all other audios when one starts playing
    Object.keys(audioRefs.current).forEach(key => {
      if (audioRefs.current[key] && parseInt(key) !== playingId) {
        audioRefs.current[key].pause();
      }
    });
  }, [playingId]);

  const handlePlayPause = (id) => {
    const audio = audioRefs.current[id];
    if (!audio) return;

    if (playingId === id) {
      audio.pause();
      setPlayingId(null);
    } else {
      audio.play();
      setPlayingId(id);
    }
  };

  const handleAudioEnd = () => {
    setPlayingId(null);
  };

  return (
    <section id="beats" className="py-16 sm:py-20 lg:py-24 bg-gradient-to-b from-black via-neutral-900 to-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold uppercase text-white mb-3 sm:mb-4 tracking-wide">
            Mes Beats
          </h2>
          <p className="text-gray-400 text-base sm:text-lg max-w-2xl mx-auto px-4">
            Explorez ma collection de productions instrumentales originales
          </p>
        </div>

        {/* Beats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {audioPortfolio.map((beat) => (
            <div
              key={beat.id}
              className="group bg-gradient-to-br from-neutral-900 to-neutral-800 border border-neutral-700 rounded-2xl overflow-hidden hover:border-violet-500/50 transition-all duration-300 hover:transform hover:-translate-y-2 shadow-xl hover:shadow-violet-500/30"
            >
              {/* Cover Image */}
              <div className="relative aspect-square overflow-hidden">
                <img
                  src={beat.cover}
                  alt={beat.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>

                {/* Play Button Overlay */}
                <button
                  onClick={() => handlePlayPause(beat.id)}
                  className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-gradient-to-r from-rose-800 to-violet-800 rounded-full flex items-center justify-center hover:scale-110 transition-transform duration-300 shadow-lg hover:shadow-rose-500/50 z-10"
                >
                  {playingId === beat.id ? (
                    <Pause size={28} className="text-white" />
                  ) : (
                    <Play size={28} className="text-white ml-1" />
                  )}
                </button>

                {/* Hidden Audio Element */}
                {beat.audioFile && (
                  <audio
                    ref={(el) => (audioRefs.current[beat.id] = el)}
                    src={beat.audioFile}
                    onEnded={handleAudioEnd}
                    preload="metadata"
                  />
                )}

                {/* Availability Badge */}
                {!beat.available && (
                  <div className="absolute top-4 right-4 px-3 py-1 bg-red-600 text-white rounded-full text-xs uppercase tracking-wider font-semibold">
                    Vendu
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-violet-400 transition-colors">
                  {beat.title}
                </h3>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {beat.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-violet-600/20 border border-violet-500/30 text-violet-300 rounded-full text-xs uppercase tracking-wider"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Details */}
                <div className="flex items-center justify-between text-sm text-gray-400 mb-4">
                  <div className="flex items-center gap-4">
                    <span>{beat.bpm} BPM</span>
                    <span>{beat.key}</span>
                    <div className="flex items-center gap-1">
                      <Clock size={14} />
                      <span>{beat.duration}</span>
                    </div>
                  </div>
                </div>

                {/* Copyright */}
                {beat.copyright && (
                  <div className="text-xs text-gray-500 mb-3 leading-relaxed text-center">
                    GAIL LLEYTON BEATS ©<br />
                    ALL RIGHTS RESERVED – COPYRIGHTS.
                  </div>
                )}

                {/* Price & CTA */}
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-white">{beat.price}</span>
                  {beat.available && (
                    <button className="px-4 py-2 bg-gradient-to-r from-rose-800 to-violet-800 text-white rounded-full font-medium text-sm uppercase tracking-wider hover:scale-105 transition-transform duration-300 shadow-lg hover:shadow-rose-500/50">
                      Démo
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-8 sm:mt-12 px-4">
          <a 
            href="mailto:Beats@GailLleyton.com?subject=Besoin%20d%27un%20beat"
            onClick={(e) => {
              e.stopPropagation();
            }}
            className="inline-block w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-white text-black rounded-full font-semibold uppercase text-xs sm:text-sm tracking-wider hover:scale-105 transition-transform duration-300"
          >
            Besoin d'un beat ? Écris-moi, on démarre la Collab.
          </a>
        </div>
      </div>
    </section>
  );
};

export default AudioPortfolio;